import { ArrowRight, X } from "lucide-react";
import { Modal } from "@design-lab/shared/ui";
import { Button } from "@design-lab/shared/ui/design-system/Button";
import { useResponsive } from "@design-lab/shared/hooks/useResponsive";
import { usePinBasedRedrawEnabled } from "@design-lab/shared/lib/config";
import { remoteImageStaticUrl } from "common/utils/remote-image";
import { USER_RETENTION_MODAL_CONTENT } from "../constants";

type UserRetentionModalProps = {
  isOpen: boolean;
  onClose: () => void;
  onRequestEdits: () => void;
};

export function UserRetentionModal({ isOpen, onClose, onRequestEdits }: UserRetentionModalProps) {
  const { isMobile } = useResponsive();
  const pinBasedRedrawEnabled = usePinBasedRedrawEnabled();

  const content = pinBasedRedrawEnabled
    ? USER_RETENTION_MODAL_CONTENT.pin
    : USER_RETENTION_MODAL_CONTENT.original;
  const deviceKey = isMobile ? "mobile" : "desktop";

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      closeOnEsc
      closeOnBackdropClick
      title={content.title}
      description="Request free artist edits to perfect your design"
      className="mx-auto flex h-auto w-full max-w-[96%] flex-col border-none shadow-none lg:max-w-3xl"
    >
      <div className="flex flex-col">
        <div className="flex items-center justify-between p-3 lg:px-8 lg:pt-7">
          <h3 className="text-base font-bold lg:text-2xl">{content.title}</h3>
          <button
            type="button"
            onClick={onClose}
            className="cursor-pointer p-0"
            data-testid="close-user-retention-modal-button"
          >
            <X className="size-5 lg:size-6" />
          </button>
        </div>

        <div className="flex flex-col gap-6 p-2 lg:p-8 lg:pt-6">
          <div
            className="border-3 flex flex-col rounded-xl border-gray-200 bg-white py-3 lg:p-4"
            data-testid="user-retention-modal-comparison"
          >
            <div className="flex items-center justify-center gap-0 lg:gap-4">
              <div
                className="flex w-2/5 flex-col items-center gap-1 lg:gap-4"
                data-testid="user-retention-modal-before-panel"
              >
                <h5 className="text-xs font-bold text-gray-800 lg:text-sm">
                  {content.beforeLabel}
                </h5>
                <img
                  src={remoteImageStaticUrl(content.imagePaths.before)}
                  alt={content.beforeAlt}
                  className="size-[150px] shrink-0 object-contain lg:size-[200px]"
                />
                {!content.sampleMessageAsList && (
                  <div
                    className="flex flex-col items-start gap-1 lg:gap-2"
                    data-testid="user-retention-modal-sample-message"
                  >
                    <h5 className="text-xs font-bold text-gray-800 lg:text-sm">
                      {content.sampleMessageLabel}
                    </h5>
                    <p className="text-xs text-gray-600 lg:text-sm">
                      {content.sampleMessageLines[0]}
                    </p>
                  </div>
                )}
              </div>

              <div className="flex size-9 items-center justify-center rounded-full bg-black lg:mt-0 lg:size-12">
                <ArrowRight className="size-5 text-white" />
              </div>

              <div
                className="flex w-2/5 flex-col items-center gap-1 lg:gap-4"
                data-testid="user-retention-modal-after-panel"
              >
                <h5 className="text-xs font-bold text-gray-800 lg:text-sm">{content.afterLabel}</h5>
                <img
                  src={remoteImageStaticUrl(content.imagePaths.after)}
                  alt={content.afterAlt}
                  className="size-[150px] shrink-0 object-contain lg:size-[200px]"
                />
                {content.changes && (
                  <div
                    className="flex flex-col items-start gap-2"
                    data-testid="user-retention-modal-changes"
                  >
                    {content.changes[deviceKey].map((change, index) => (
                      <div key={change} className="flex items-center gap-1">
                        <span className="flex size-5 items-center justify-center rounded-full bg-emerald-500 text-sm text-white">
                          {index + 1}
                        </span>
                        <h5 className="text-xs font-bold text-gray-800 lg:text-sm">{change}</h5>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {content.sampleMessageAsList && (
              <div
                className="mt-4 flex flex-col gap-2 px-2 lg:px-0"
                data-testid="user-retention-modal-sample-message"
              >
                <h5 className="text-xs font-bold text-gray-800 lg:text-sm">
                  {content.sampleMessageLabel}
                </h5>
                <ol className="list-decimal pl-5">
                  {content.sampleMessageLines.map((line, index) => (
                    <li
                      key={index}
                      className="text-xs text-gray-600 lg:text-sm"
                      data-testid="user-retention-modal-sample-message-item"
                    >
                      {line}
                    </li>
                  ))}
                </ol>
              </div>
            )}
          </div>

          <div className="flex gap-3">
            <Button
              onPress={onRequestEdits}
              size="2xl"
              className="w-[65%] rounded-xl bg-[#2A4DD0] text-base font-bold hover:bg-[#2A4DD0]/90 lg:rounded"
              data-testid="request-artist-edits-for-free"
            >
              {content.buttonText[deviceKey]}
            </Button>
            <Button
              onPress={onClose}
              variant="outline"
              size="2xl"
              className="w-[35%] rounded-xl text-base font-bold text-gray-950 lg:rounded"
              data-testid="continue-button"
            >
              Continue
            </Button>
          </div>
        </div>
      </div>
    </Modal>
  );
}
